import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { listOrders, updateOrderStatus, deleteOrder, setOrderDeliveryDate } from "@/lib/admin.functions";

function fmtDate(d: string) {
  return new Date(`${d}T12:00:00Z`).toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Africa/Johannesburg" });
}

function DeliveryDateForm({ current, saving, onSave }: { current: string | null; saving: boolean; onSave: (d: string) => void }) {
  const [value, setValue] = useState(current ?? "");
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      <Input type="date" value={value} onChange={(e) => setValue(e.target.value)} className="h-9 w-auto" />
      <Button size="sm" disabled={!value || value === current || saving} onClick={() => onSave(value)}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & notify client"}
      </Button>
    </div>
  );
}
import { formatPrice } from "@/lib/shop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/orders")({
  component: AdminOrders,
});

const STATUSES = ["pending", "paid", "processing", "shipped", "completed", "cancelled", "refunded", "failed"] as const;
const PT_LABEL: Record<string, string> = { standard: "Standard", physio: "Physio Guided", mixed: "Mixed" };

function badgeClass(status: string) {
  if (status === "paid" || status === "completed") return "bg-emerald-100 text-emerald-800";
  if (status === "pending" || status === "processing" || status === "shipped") return "bg-amber-100 text-amber-800";
  if (status === "refunded") return "bg-sky-100 text-sky-800";
  return "bg-rose-100 text-rose-800";
}

function AdminOrders() {
  const fetchOrders = useServerFn(listOrders);
  const setStatus = useServerFn(updateOrderStatus);
  const setDelivery = useServerFn(setOrderDeliveryDate);
  const removeOrder = useServerFn(deleteOrder);
  const qc = useQueryClient();

  const [q, setQ] = useState("");
  const [status, setStatusFilter] = useState("all");
  const [purchaseType, setPurchaseType] = useState("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isPending } = useQuery({ queryKey: ["admin-orders"], queryFn: () => fetchOrders() });

  const statusMut = useMutation({
    mutationFn: (v: { id: string; status: any }) => setStatus({ data: v }),
    onSuccess: () => { toast.success("Order updated"); qc.invalidateQueries({ queryKey: ["admin-orders"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Could not update the order"),
  });
  const deliveryMut = useMutation({
    mutationFn: (v: { id: string; date: string }) => setDelivery({ data: v }),
    onSuccess: (r: any) => {
      toast.success(r?.emailed ? "Delivery date saved and emailed to the client" : "Delivery date saved");
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not save the delivery date"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => removeOrder({ data: { id } }),
    onSuccess: () => { toast.success("Order deleted"); qc.invalidateQueries({ queryKey: ["admin-orders"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Could not delete the order"),
  });

  const orders = (data?.orders ?? []) as any[];
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return orders.filter((o) => {
      if (status !== "all" && o.status !== status) return false;
      if (purchaseType !== "all" && o.purchase_type !== purchaseType) return false;
      if (!term) return true;
      return [o.order_number, o.customer_name, o.email, o.phone, o.delivery_address]
        .filter(Boolean)
        .some((v: string) => String(v).toLowerCase().includes(term));
    });
  }, [orders, q, status, purchaseType]);

  const paidTotal = filtered.filter((o) => o.payment_status === "paid").reduce((s, o) => s + Number(o.total ?? 0), 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Orders</h1>
          <p className="text-sm text-muted-foreground">Shop orders, payments and fulfilment.</p>
        </div>
        <div className="text-right text-sm">
          <div className="text-muted-foreground">Paid revenue (filtered)</div>
          <div className="text-xl font-semibold">{formatPrice(paidTotal)}</div>
        </div>
      </header>

      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order number, name, email, phone" className="pl-9" />
        </div>
        <select value={status} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={purchaseType} onChange={(e) => setPurchaseType(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
          <option value="all">All purchase types</option>
          <option value="standard">Standard</option>
          <option value="physio">Physio Guided</option>
          <option value="mixed">Mixed</option>
        </select>
      </div>

      {isPending ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed p-12 text-center text-sm text-muted-foreground">No orders match these filters yet.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-card">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((o) => (
                <>
                  <tr key={o.id} className="align-top">
                    <td className="px-4 py-3">
                      <button onClick={() => setOpenId(openId === o.id ? null : o.id)} className="flex items-center gap-1 font-medium">
                        {o.order_number}
                        <ChevronDown className={`h-4 w-4 transition ${openId === o.id ? "rotate-180" : ""}`} />
                      </button>
                      <div className="text-xs text-muted-foreground">
                        {new Date(o.created_at).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg" })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{o.customer_name}</div>
                      <div className="text-xs text-muted-foreground">{o.email}</div>
                      <div className="text-xs text-muted-foreground">{o.phone}</div>
                      {o.estimated_delivery_at ? (
                        <div className="mt-1 text-xs font-medium text-primary">Delivery: {fmtDate(o.estimated_delivery_at)}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">{PT_LABEL[o.purchase_type] ?? o.purchase_type}</td>
                    <td className="px-4 py-3 font-medium">{formatPrice(Number(o.total))}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass(o.payment_status)}`}>{o.payment_status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={o.status}
                        onChange={(e) => statusMut.mutate({ id: o.id, status: e.target.value })}
                        className="h-9 rounded-md border border-input bg-background px-2 text-xs"
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { if (confirm(`Delete order ${o.order_number}? This cannot be undone.`)) deleteMut.mutate(o.id); }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                  {openId === o.id ? (
                    <tr key={`${o.id}-details`} className="bg-muted/30">
                      <td colSpan={7} className="px-4 py-4">
                        <div className="grid gap-6 md:grid-cols-2">
                          <div>
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Delivery</h3>
                            <p className="mt-1">{o.delivery_address}{o.delivery_city ? `, ${o.delivery_city}` : ""}{o.delivery_postal_code ? `, ${o.delivery_postal_code}` : ""}</p>
                            {o.billing_address ? <p className="mt-1 text-xs text-muted-foreground">Billing: {o.billing_address}</p> : null}
                            {o.delivery_notes ? <p className="mt-1 text-xs text-muted-foreground">Notes: {o.delivery_notes}</p> : null}
                            <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Delivery date</h3>
                            <DeliveryDateForm
                              current={o.estimated_delivery_at}
                              saving={deliveryMut.isPending}
                              onSave={(date) => deliveryMut.mutate({ id: o.id, date })}
                            />
                            <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Payment</h3>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {o.payment_provider} · {o.payment_mode} · ref {o.provider_reference ?? "n/a"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Subtotal {formatPrice(Number(o.subtotal))} · Delivery {formatPrice(Number(o.shipping))}
                            </p>
                          </div>
                          <div>
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Items</h3>
                            <ul className="mt-1 space-y-2">
                              {(o.order_items ?? []).map((it: any) => (
                                <li key={it.id} className="flex justify-between gap-4">
                                  <span>
                                    {it.name} × {it.qty}
                                    <span className="block text-xs text-muted-foreground">
                                      {PT_LABEL[it.purchase_type] ?? it.purchase_type}
                                      {Object.entries((it.options ?? {}) as Record<string, string>).length
                                        ? ` · ${Object.entries(it.options as Record<string, string>).map(([k, v]) => `${k}: ${v}`).join(", ")}`
                                        : ""}
                                    </span>
                                  </span>
                                  <span className="whitespace-nowrap">{formatPrice(Number(it.line_total))}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
